import {ReactElement} from 'react';
import {
    Alert,
    Stack,
} from '@mantine/core';
import {DataTableColumn} from 'mantine-datatable';
import {AiOutlineWarning} from 'react-icons/ai';

import {
    MantineDataTable,
    SimpleTableBatchAction,
} from '@flexinet/ui-components';

import {useTestDefinitions} from '../hooks/useTestDefinitions.ts';
import {useTestDefinitionsPaging} from '../hooks/useTestDefinitionsPaging.ts';
import {useTestDefinitionsSearch} from '../hooks/useTestDefinitionsSearch.ts';
import {useTestDefinitionsSelection} from '../hooks/useTestDefinitionsSelection.ts';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions.ts';
import {rbacActions} from '../../../config/rbac/actions.ts';
import {TestDefinition} from '../types.ts';

export const RecordsTable = ({
                                 extraTools,
                                 settings,
                             }: {
    extraTools?: ReactElement[],
    settings?: { label: string, onClick: () => void }[]
}) => {

    const {allowedActions} = useVerifiedPermissions();

    const {
        columns,
        records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
    } = useTestDefinitions();

    const {
        page,
        limit,
        totalCount,
        handleItemsPerPageChange,
        handlePageChange,
    } = useTestDefinitionsPaging(records.length);

    // Same store instance the list hook filters on (FNP-815).
    const {handleFilterByKeyword} = useTestDefinitionsSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection,
    } = useTestDefinitionsSelection();

    let selectedRecords: TestDefinition[] = [];
    if (allowedActions.includes(rbacActions.DeleteTestDefinition)) {
        selectedRecords = records.filter(record => selection[record.id] === true);
    }

    // A failed load is stated rather than rendered as an empty catalog — an
    // empty table and a broken query look identical otherwise.
    if (isError) {
        return (
            <Alert
                icon={<AiOutlineWarning/>}
                color='red'
                title='Could not load test definitions'
            >
                The Testing API did not respond. Use Reload to try again.
            </Alert>
        );
    }

    return (
        <Stack>
            <SimpleTableBatchAction
                limit={limit}
                handleItemsPerPageChange={(pageSize) => handleItemsPerPageChange(Number(pageSize))}
                settings={settings}
                handleFilterByKeyword={handleFilterByKeyword}
                extraTools={extraTools}
            />

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                totalRecords={totalCount}
                recordsPerPage={limit}
                page={page}
                onPageChange={handlePageChange}
                // Sorting is WIRED, not merely declared on the columns: a
                // `sortable: true` column with no sortStatus/onSortStatusChange
                // renders the affordance and does nothing when clicked.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                paginationText={({
                                     from,
                                     to,
                                     totalRecords
                                 }) => `Records ${from} - ${to} of ${totalRecords}`}
                paginationSize={'md'}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    (items as TestDefinition[]).forEach(item => {
                        handleRowSelection(item.id, true);
                    });
                }}
                fetching={loading}
                noRecordsText='No test definitions'
            />
        </Stack>
    );
};
